import { useCallback } from "react";
import { useLocation } from "wouter";

import { notify } from "../../../components/Notification";
import {
  ErrorResponse,
  exportUserData,
  useDeactivateUserMutation,
  useDeleteUserMutation,
  useGetAccountStatsQuery,
  useRevokeAllSessionsMutation,
} from "../../../services";
import { useUser } from "../../../context/UserContext";
import { UseAccountManagementResult } from "./types";

export const useAccountManagement = (): UseAccountManagementResult => {
  const { endSession } = useUser();
  const [, navigate] = useLocation();

  const { data: accountStats } = useGetAccountStatsQuery();
  const [deactivateUser] = useDeactivateUserMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [revokeAllSessions, { isLoading: isRevokingSessions }] =
    useRevokeAllSessionsMutation();

  /**
   * Stays on axios: `/auth/export` streams a zip, which needs
   * `responseType: "blob"` — something `axiosBaseQuery` deliberately doesn't
   * forward — and its only effect is a browser download, not cached state.
   */
  const handleExportData = useCallback(async () => {
    try {
      await exportUserData();
      notify.success("Account data exported successfully!");
    } catch (err: unknown) {
      notify.apiError(err as ErrorResponse);
    }
  }, []);

  const handleDeactivate = useCallback(async () => {
    try {
      await deactivateUser().unwrap();
      notify.success("Account successfully deactivated!");
      endSession();
      navigate("/");
    } catch {
      // errorToastMiddleware raises the toast; keep the modal open.
    }
  }, [deactivateUser, endSession, navigate]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteUser().unwrap();
      notify.success("Account successfully deleted!");
      endSession();
      navigate("/");
    } catch {
      // errorToastMiddleware raises the toast; keep the modal open.
    }
  }, [deleteUser, endSession, navigate]);

  /**
   * Resolves to whether the revoke succeeded, so the caller can keep the
   * confirmation modal open on failure. The refreshed session count arrives on
   * its own — the mutation invalidates `AccountStats`.
   */
  const handleRevokeAllSessions = useCallback(async () => {
    try {
      const result = await revokeAllSessions().unwrap();
      notify.success(
        `Successfully revoked ${result.revokedSessions} session(s)!`,
      );
      return true;
    } catch {
      return false;
    }
  }, [revokeAllSessions]);

  return {
    accountStats: accountStats ?? null,
    isRevokingSessions,
    handleExportData,
    handleDeactivate,
    handleDelete,
    handleRevokeAllSessions,
  };
};
